import { useRef } from "react";

import { isEqual } from "lodash";
import uniq from "lodash/uniq";
import { useDispatch } from "react-redux";

import { showErrorToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import {
    REGEXP_COMPETITORS_DOMAINS,
    REGEXP_VALID_DOMAIN_REMOVED_PROTOCOLS,
    TRIM_QUOTES_REG_EX,
    TRIM_SUB_SITE_REG_EX,
} from "app/pages/activation/components/SetupModal/CompetitorsStep/constants";
import { competitorsStepTexts } from "app/pages/activation/components/SetupModal/CompetitorsStep/texts";
import type { CompetitorsMap, CompetitorSchema } from "app/pages/activation/components/SetupModal/CompetitorsStep/types";
import { REGEX_PASTE_KW } from "app/pages/activation/components/Topics/Modal/Flow/Advanced/constants";
import useIconsMutation from "app/pages/activation/queries/useIconsMutation";

import { ActivationComponentAssets } from "../../helpers/ActivationAssets";

const usePasteWithImages = (
    existingDomains: CompetitorSchema[],
    competitorsList: CompetitorsMap,
    selectedPrimaryEntityName: string,
) => {
    const dispatch = useDispatch();

    const { mutateAsync: getIcons, isPending: isLoading } = useIconsMutation();

    const previousPaste = useRef([]);

    const onPasteDataUpdater = async (e: ClipboardEvent) => {
        const itemsString = e.clipboardData.getData("Text");

        const maxCompetitors = ActivationComponentAssets.competitorsLimit;
        const limit = maxCompetitors - existingDomains.length <= 0 ? 0 : maxCompetitors - existingDomains.length;

        const pastedItems = itemsString.trim().split(REGEX_PASTE_KW);

        const newSelectedItems: string[] = [];
        const newCompetitorsList: CompetitorsMap = new Map();

        if (isEqual(previousPaste.current, pastedItems)) {
            return {
                updatedSelectedCompetitorsList: existingDomains,
                updatedCompetitorsList: competitorsList,
            };
        }

        pastedItems.forEach((domain) => {
            if (newSelectedItems.length >= limit) {
                return;
            }

            const validDomain = domain
                .replace(REGEXP_VALID_DOMAIN_REMOVED_PROTOCOLS, "$1")
                .replace(TRIM_SUB_SITE_REG_EX, "")
                .replace(TRIM_QUOTES_REG_EX, "");

            if (
                validDomain &&
                REGEXP_COMPETITORS_DOMAINS.test(validDomain) &&
                validDomain !== selectedPrimaryEntityName
            ) {
                newSelectedItems.push(validDomain);
            }

            if (validDomain && competitorsList.has(validDomain)) {
                competitorsList.delete(validDomain);
            }
        });

        const uniqNewSelectedItems = uniq(newSelectedItems);
        const removedDuplicationCount = newSelectedItems.length - uniqNewSelectedItems.length;

        if (pastedItems.length > limit) {
            dispatch(
                showErrorToast(
                    translate(competitorsStepTexts.errorLimitReached, {
                        added: uniqNewSelectedItems.length,
                        count: pastedItems.length,
                    }),
                ),
            );
        }

        if (removedDuplicationCount && pastedItems.length < limit) {
            dispatch(
                showErrorToast(
                    translate(competitorsStepTexts.errorRemoveDuplication, {
                        added: uniqNewSelectedItems.length,
                        removed: removedDuplicationCount,
                    }),
                ),
            );
        }

        if (!uniqNewSelectedItems.length) {
            dispatch(showErrorToast(translate(competitorsStepTexts.errorInvalidPaseData)));

            return {
                updatedSelectedCompetitorsList: existingDomains,
                updatedCompetitorsList: competitorsList,
            };
        }

        const icons = await getIcons(uniqNewSelectedItems);

        const domainsIcons = Object.keys(icons);
        const newCompetitorsArray: CompetitorSchema[] = [];

        domainsIcons.forEach((key: string) => {
            const newCompetitor = {
                name: key,
                image: icons[key],
                isAiGenerated: false,
                reason: translate(competitorsStepTexts.defaultReason),
            };

            newCompetitorsList.set(key, newCompetitor);
            newCompetitorsArray.push(newCompetitor);
        });

        const updatedCompetitorsList: CompetitorsMap = new Map([...newCompetitorsList, ...competitorsList]);

        const updatedSelectedCompetitorsList: CompetitorSchema[] = uniq([...newCompetitorsArray, ...existingDomains]);

        previousPaste.current = pastedItems;

        return {
            updatedSelectedCompetitorsList,
            updatedCompetitorsList,
        };
    };

    return {
        onPasteDataUpdater,
        isLoading,
    };
};

export default usePasteWithImages;
